/** Mobile-first breakpoints: `base` applies from 0, the others from the project's Tailwind screens (640, 768, 1025, 1280, 1536px) */
export const breakpoints = ['base', 'sm', 'md', 'lg', 'xl', '2xl'] as const;
export type Breakpoint = (typeof breakpoints)[number];

/** One value, or a value per breakpoint; a breakpoint without a value keeps the value of the one below it */
export type Responsive<T> = T | Partial<Record<Breakpoint, T>>;

export type LayoutSpace = 0 | '0' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type LayoutMargin = LayoutSpace | 'auto';
/** Fixed lengths: `2xs` 120px, `xs` 160px, `sm` 240px, `md` 320px, `lg` 480px, `xl` 640px, `2xl` 768px */
export type LayoutSizeStep = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type LayoutSize = 'full' | 'auto' | 'fit' | 'min' | 'max' | LayoutSizeStep;
export type LayoutRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type LayoutBorderColor = 'primary' | 'secondary' | 'tertiary' | 'subdued' | 'subdued-extra' | 'positive' | 'warning' | 'negative' | 'informative' | 'highlighted';
export type LayoutAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type LayoutBorderSide = 'top' | 'right' | 'bottom' | 'left' | 'x' | 'y';
export type LayoutBorderStyle = 'solid' | 'dashed';
export type LayoutFlex = 'fill' | 'auto' | 'none';
export type LayoutJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
export type LayoutElement = 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'main' | 'nav' | 'ul' | 'ol' | 'li' | 'form' | 'span' | 'label' | 'fieldset' | 'legend' | 'dl' | 'dt' | 'dd';

export type BoxProps = {
    as?: LayoutElement;
    p?: Responsive<LayoutSpace>;
    px?: Responsive<LayoutSpace>;
    py?: Responsive<LayoutSpace>;
    pt?: Responsive<LayoutSpace>;
    pr?: Responsive<LayoutSpace>;
    pb?: Responsive<LayoutSpace>;
    pl?: Responsive<LayoutSpace>;
    m?: Responsive<LayoutMargin>;
    mx?: Responsive<LayoutMargin>;
    my?: Responsive<LayoutMargin>;
    mt?: Responsive<LayoutMargin>;
    mr?: Responsive<LayoutMargin>;
    mb?: Responsive<LayoutMargin>;
    ml?: Responsive<LayoutMargin>;
    w?: LayoutSize;
    h?: LayoutSize;
    miw?: LayoutSize;
    maw?: LayoutSize;
    mih?: LayoutSize;
    mah?: LayoutSize;
    /** A 1px border in `--color-border-secondary` */
    withBorder?: boolean;
    /** The border color, from the `--color-border-*` tokens; implies `withBorder` */
    borderColor?: LayoutBorderColor;
    /** Draw the border on one side or a pair (`x`, `y`) only; implies `withBorder`. Default: all four */
    borderSide?: LayoutBorderSide;
    /** Implies `withBorder`. Default `solid` */
    borderStyle?: LayoutBorderStyle;
    radius?: LayoutRadius;
    /** As a flex child: `fill` takes the free space (`1 1 0`, may shrink below its content), `none` never grows or shrinks */
    flex?: LayoutFlex;
    /** As a flex or grid child: its own cross-axis alignment */
    alignSelf?: LayoutAlign;
};
